import React, { useMemo, useState } from 'react';

import {
  Alert,
  Button,
  Divider,
  Group,
  LoadingOverlay,
  Stack,
  TextInput,
  Title,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconAlertCircle } from '@tabler/icons-react';
import Drawer from 'components/Drawer';
import FlowTriggerSelect from 'components/form-fields/select-fields/FlowTriggerSelect';
import { useAuth } from 'context/auth-context';
import useFlowDynamicTemplateVariables from 'hooks/flows/useFlowDynamicTemplateVariables';
import useCreateNotificationMessageTemplate from 'hooks/notificationMessageTemplate/useCreateNotificationTemplateMessage';
import useNotificationMessageTemplate from 'hooks/notificationMessageTemplate/useNotificationTemplateMessage';
import useUpdateNotificationMessageTemplate from 'hooks/notificationMessageTemplate/useUpdateNotificationTemplateMessage';
import notificationHandler from 'utils/notifications';
import { v4 as uuidv4 } from 'uuid';

import { NotificationMessageTemplateAutoCompleteValue } from '../../FlowBuilder/types';

interface FormValues {
  notificationMessageTemplateId: string;
  notificationType: 'Email' | 'SMS';
  description: string;
  contents: string;
}

const recipientTemplateVariables: { [key: string]: string } = {
  'Recipient First Name': 'RECIPIENT_FIRST_NAME',
  'Recipient Last Name': 'RECIPIENT_LAST_NAME',
  'Recipient Email Address': 'RECIPIENT_EMAIL',
  'Recipient Phone Number': 'RECIPIENT_PHONE_NUMBER',
};

const gymTemplateVariables: { [key: string]: string } = {
  'Gym Name': 'GYM_NAME',
  'Gym Address': 'GYM_ADDRESS',
  'Gym Logo': 'GYM_LOGO',
  'Gym Email': 'GYM_EMAIL',
  [`Today's Date`]: `TODAYS_DATE`,
};

const DrawerSMSNotificationMessageTemplate = ({
  formId,
  opened,
  notificationMessageTemplateId,
  closeHandler,
  handleNotificationMessageTemplateChange,
  trigger,
  recipients,
  permission = true,
}: {
  formId: string;
  opened: boolean;
  notificationMessageTemplateId?: string | null;
  closeHandler: () => void;
  trigger?: string;
  recipients?: string;
  permission?: boolean;
  handleNotificationMessageTemplateChange?: (
    notificationMessageTemplate: NotificationMessageTemplateAutoCompleteValue,
  ) => void;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { session } = useAuth();

  const [dynamicTemplateManualTrigger, setDynamicTemplateManualTrigger] = useState<string | null>();

  const { data: dynamicTemplatesData, isSuccess: dynamicTemplatesIsSuccess } =
    useFlowDynamicTemplateVariables(trigger ? trigger : dynamicTemplateManualTrigger);

  const handleSelectDynamicManualTemplate = (selectedTrigger: string): void => {
    setDynamicTemplateManualTrigger(selectedTrigger);
  };

  const form = useForm<FormValues>({
    initialValues: {
      notificationMessageTemplateId: uuidv4(),
      notificationType: 'SMS',
      description: '',
      contents: '',
    },

    validate: {
      description: value =>
        !value || value.length > 255 ? 'Description must be between 1-255 characters.' : null,
      contents: value => (!value || value === '<p></p>' ? 'This field may not be blank.' : null),
    },
  });

  const {
    data: notificationMessageTemplateData,
    isLoading: isNotificationMessageTemplateDataLoading,
    isFetching: isNotificationMessageTemplateDataFetching,
    isSuccess: isNotificationMessageTemplateDataSuccess,
  } = useNotificationMessageTemplate(notificationMessageTemplateId || '');

  const createMutation = useCreateNotificationMessageTemplate();
  const updateMutation = useUpdateNotificationMessageTemplate();

  const { successNotification, errorNotification } = notificationHandler();

  const staticTemplateVariables = useMemo(() => {
    if (recipients === 'Staff') {
      return gymTemplateVariables;
    }

    return {
      ...recipientTemplateVariables,
      ...gymTemplateVariables,
    };
  }, [recipients]);

  const templateVariables = useMemo(() => {
    if (dynamicTemplatesIsSuccess && dynamicTemplatesData) {
      const parsedDynamicTemplates = JSON.parse(dynamicTemplatesData.data);

      return {
        ...staticTemplateVariables,
        ...parsedDynamicTemplates,
      };
    }

    return {};
  }, [dynamicTemplatesData, dynamicTemplatesIsSuccess, staticTemplateVariables]);

  const handleTemplateVariableClick = (variable: string): void => {
    const templateToInsert = templateVariables[variable];
    const currentContents = form.values.contents;
    form.setFieldValue('contents', currentContents + templateToInsert + ' ');
  };

  const handleCloseAndReset = (): void => {
    closeHandler();
    form.reset();
    setDynamicTemplateManualTrigger(null);
  };

  const handleSubmit = (values: FormValues): void => {
    if (notificationMessageTemplateId) {
      updateMutation.mutate(
        { ...values, subject: null },
        {
          onSuccess: response => {
            successNotification(
              'NotificationMessageTemplateData template update success',
              `${response.data.description} successfully updated.`,
            );
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification(
              'NotificationMessageTemplateData template update failed',
              error.response?.data.message,
            );
          },
        },
      );
    } else if (session?.staff) {
      createMutation.mutate(
        {
          ...values,
          notificationMessageTemplateId: uuidv4(),
          subject: null,
        },
        {
          onSuccess: response => {
            successNotification(
              'NotificationMessageTemplateData template creation success',
              `${response.data.description} successfully created.`,
            );
            const selectedNotificationMessageTemplate = {
              notificationMessageTemplateId: response.data.notificationMessageTemplateId
                ? response.data.notificationMessageTemplateId
                : '',
              label: response.data ? response.data.description : '',
              value: '',
            };
            if (handleNotificationMessageTemplateChange) {
              handleNotificationMessageTemplateChange(selectedNotificationMessageTemplate);
            }
            handleCloseAndReset();
          },
          onError: error => {
            errorNotification(
              'NotificationMessageTemplateData template creation failed',
              error.response?.data.message,
            );
          },
        },
      );
    }
  };

  useShallowEffect(() => {
    if (
      notificationMessageTemplateData?.data &&
      isNotificationMessageTemplateDataSuccess &&
      notificationMessageTemplateId &&
      opened
    ) {
      form.setFieldValue('contents', notificationMessageTemplateData.data.contents);
      form.setFieldValue('description', notificationMessageTemplateData.data.description);
      form.setFieldValue(
        'notificationMessageTemplateId',
        notificationMessageTemplateData.data.notificationMessageTemplateId,
      );
      form.setFieldValue('notificationType', notificationMessageTemplateData.data.notificationType);
    }
  }, [notificationMessageTemplateData?.data, notificationMessageTemplateId, opened]);

  return (
    <Drawer
      opened={opened}
      onClose={handleCloseAndReset}
      size={900}
      title={
        <Title order={3}>
          {notificationMessageTemplateId
            ? notificationMessageTemplateData?.data?.description
            : 'Create SMS Notification Message Template'}
        </Title>
      }
      closeOnClickOutside
      footer={
        <Group position='right'>
          <Button variant='subtle' onClick={handleCloseAndReset}>
            Cancel
          </Button>
          <Button
            form={formId}
            type='submit'
            disabled={
              !permission ||
              isNotificationMessageTemplateDataLoading ||
              isNotificationMessageTemplateDataFetching
            }
            loading={createMutation.isLoading || updateMutation.isLoading}
          >
            Save
          </Button>
        </Group>
      }
    >
      <LoadingOverlay
        visible={isNotificationMessageTemplateDataLoading || isNotificationMessageTemplateDataFetching}
      />
      <form id={formId} onSubmit={form.onSubmit(handleSubmit)}>
        <Stack spacing='md'>
          <TextInput label='Description' {...form.getInputProps('description')} disabled={!permission} />

          <Divider mx='-xl' />

          <FlowTriggerSelect
            label='Trigger'
            placeholder='Select a trigger'
            disabled={!!trigger}
            value={!!trigger ? trigger : dynamicTemplateManualTrigger}
            onChange={handleSelectDynamicManualTemplate}
          />

          {!trigger && !dynamicTemplateManualTrigger && (
            <Alert icon={<IconAlertCircle size='1rem' />} variant='filled'>
              Select a Trigger to show available dynamic template variables
            </Alert>
          )}

          <Stack spacing='xs'>
            <Group spacing='xs'>
              {Object.keys(templateVariables).map(variable => (
                <Button
                  key={variable}
                  radius='sm'
                  size='xs'
                  compact
                  onClick={() => handleTemplateVariableClick(variable)}
                  disabled={!permission}
                >
                  {variable}
                </Button>
              ))}
            </Group>
          </Stack>

          {form.errors.contents && (
            <Alert icon={<IconAlertCircle size={theme.fontSizes.lg} />} color='red'>
              {form.errors.contents}
            </Alert>
          )}

          <TextInput
            label='Content'
            {...form.getInputProps('contents')}
            disabled={!permission}
            autoComplete='off'
          />
        </Stack>
      </form>
    </Drawer>
  );
};

export default DrawerSMSNotificationMessageTemplate;
